// src/pages/StockTransfersPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Package, Truck, CheckCircle, ChevronDown, ChevronUp, X, DollarSign, CreditCard } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { stockTransferApi, storeApi, productApi } from '../api';

const STATUS_CONFIG = {
  pending:         { label: '待确认',  color: 'bg-yellow-100 text-yellow-700' },
  shipped:         { label: '已发货',  color: 'bg-blue-100 text-blue-700' },
  received:        { label: '已收货',  color: 'bg-purple-100 text-purple-700' },
  payment_pending: { label: '待收款',  color: 'bg-orange-100 text-orange-700' },
  completed:       { label: '已完成',  color: 'bg-green-100 text-green-700' },
  cancelled:       { label: '已取消',  color: 'bg-gray-100 text-gray-500' }
};

const StockTransfersPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';
  const isBranch = !isHQ;

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedNo, setExpandedNo] = useState(null);
  const [detailMap, setDetailMap] = useState({});

  // 发起调货申请
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState([]);
  const [createForm, setCreateForm] = useState({ notes: '', items: [] });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // 收货弹窗
  const [showReceive, setShowReceive] = useState(false);
  const [receiveTransferNo, setReceiveTransferNo] = useState('');
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [receiving, setReceiving] = useState(false);

  // 付款弹窗
  const [showPay, setShowPay] = useState(false);
  const [payTransferNo, setPayTransferNo] = useState('');
  const [payForm, setPayForm] = useState({ paid_amount: '', notes: '' });
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    loadTransfers();
    // 分店加载总店商品，总店不需要
    if (isBranch) loadHQProducts();
  }, [filterStatus]);

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const res = await stockTransferApi.getList(filterStatus ? { status: filterStatus } : {});
      if (res.success) setTransfers(res.data);
    } catch (err) {
      console.error('加载划拨单失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 加载总店有库存的商品（分店申请用）
  const loadHQProducts = async () => {
    try {
        const res = await productApi.getHQStock();
        if (res.success) setProducts(res.data);
    } catch (err) { console.error('加载总店商品失败:', err); }
  };

  const toggleDetail = async (transfer_no) => {
    if (expandedNo === transfer_no) { setExpandedNo(null); return; }
    setExpandedNo(transfer_no);
    if (!detailMap[transfer_no]) {
      try {
        const res = await stockTransferApi.getDetail(transfer_no);
        if (res.success) setDetailMap(prev => ({ ...prev, [transfer_no]: res.data }));
      } catch (err) { console.error(err); }
    }
  };

  const refreshTransfer = (transfer_no) => {
    loadTransfers();
    setDetailMap(prev => { const n = { ...prev }; delete n[transfer_no]; return n; });
  };

  // 发起调货申请
  const addItem = () => {
    setCreateForm(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', quantity: 1, product_name: '', stock: 0 }]
    }));
  };

  const removeItem = (idx) => {
    setCreateForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const updateItem = (idx, field, value) => {
    setCreateForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'product_id') {
        const product = products.find(p => p.id === parseInt(value));
        if (product) { items[idx].product_name = product.name; items[idx].stock = product.stock; }
      }
      return { ...prev, items };
    });
  };

  const handleCreate = async () => {
    if (createForm.items.length === 0) { setCreateError('请至少添加一个商品'); return; }
    for (const item of createForm.items) {
      if (!item.product_id) { setCreateError('请选择商品'); return; }
      if (item.quantity < 1) { setCreateError('数量必须大于0'); return; }
      if (parseInt(item.quantity) > item.stock) { setCreateError(`商品「${item.product_name}」总店库存不足`); return; }
    }
    setCreating(true);
    setCreateError('');
    try {
      const res = await stockTransferApi.create({
        notes: createForm.notes,
        items: createForm.items.map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(i.quantity) }))
      });
      if (res.success) {
        setShowCreate(false);
        setCreateForm({ notes: '', items: [] });
        loadTransfers();
      } else {
        setCreateError(res.message || '创建失败');
      }
    } catch (err) {
      setCreateError(err.response?.data?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  // 总店确认发货
  const handleShip = async (transfer_no) => {
    if (!window.confirm(`确认发货 ${transfer_no}？发货后将扣减总店库存。`)) return;
    try {
      const res = await stockTransferApi.ship(transfer_no);
      if (res.success) { alert('发货成功'); refreshTransfer(transfer_no); }
      else alert(res.message || '发货失败');
    } catch (err) { alert(err.response?.data?.message || '发货失败'); }
  };

  // 分店确认收货
  const openReceive = async (transfer_no) => {
    setReceiveTransferNo(transfer_no);
    setReceiveNotes('');
    try {
      const res = await stockTransferApi.getDetail(transfer_no);
      if (res.success) {
        setReceiveItems(res.data.map(t => ({
          transfer_id: t.id,
          product_name: t.product_name,
          quantity: t.quantity,
          actual_quantity: t.quantity
        })));
      }
    } catch (err) { console.error(err); }
    setShowReceive(true);
  };

  const handleReceive = async () => {
    setReceiving(true);
    try {
      const res = await stockTransferApi.receive(receiveTransferNo, {
        received_items: receiveItems.map(i => ({ transfer_id: i.transfer_id, actual_quantity: parseInt(i.actual_quantity) })),
        notes: receiveNotes
      });
      if (res.success) {
        alert('已确认收货，请提交付款通知');
        setShowReceive(false);
        refreshTransfer(receiveTransferNo);
      } else {
        alert(res.message || '收货失败');
      }
    } catch (err) { alert(err.response?.data?.message || '收货失败'); }
    finally { setReceiving(false); }
  };

  // 分店提交付款通知
  const openPay = (transfer_no, total_amount) => {
    setPayTransferNo(transfer_no);
    setPayForm({ paid_amount: total_amount ? parseFloat(total_amount).toFixed(2) : '', notes: '' });
    setShowPay(true);
  };

  const handlePay = async () => {
    if (!payForm.paid_amount) { alert('请输入付款金额'); return; }
    setPaying(true);
    try {
      const res = await stockTransferApi.pay(payTransferNo, {
        paid_amount: parseFloat(payForm.paid_amount),
        notes: payForm.notes
      });
      if (res.success) {
        alert('已通知总店，等待确认收款');
        setShowPay(false);
        refreshTransfer(payTransferNo);
      } else {
        alert(res.message || '提交失败');
      }
    } catch (err) { alert(err.response?.data?.message || '提交失败'); }
    finally { setPaying(false); }
  };

  // 总店确认收款
  const handleConfirmPayment = async (transfer_no) => {
    if (!window.confirm(`确认已收款 ${transfer_no}？确认后分店库存将自动更新。`)) return;
    try {
      const res = await stockTransferApi.confirmPayment(transfer_no);
      if (res.success) { alert('已确认收款，分店库存已更新'); refreshTransfer(transfer_no); }
      else alert(res.message || '操作失败');
    } catch (err) { alert(err.response?.data?.message || '操作失败'); }
  };

  // 取消
  const handleCancel = async (transfer_no) => {
    if (!window.confirm('确定取消该调货申请？')) return;
    try {
      const res = await stockTransferApi.cancel(transfer_no);
      if (res.success) { alert('已取消'); refreshTransfer(transfer_no); }
      else alert(res.message || '取消失败');
    } catch (err) { alert(err.response?.data?.message || '取消失败'); }
  };

  const filterTabs = isHQ
    ? [['', '全部'], ['pending', '待确认'], ['shipped', '已发货'], ['payment_pending', '待收款'], ['completed', '已完成']]
    : [['', '全部'], ['pending', '待确认'], ['shipped', '待收货'], ['received', '待付款'], ['payment_pending', '已付款'], ['completed', '已完成']];

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">库存划拨</h2>
          <p className="text-gray-600 mt-1">
            {isHQ ? '管理所有门店的调货申请' : '向总店申请调货'}
          </p>
        </div>
        {isManager && isBranch && (
          <button
            onClick={() => { setShowCreate(true); setCreateError(''); setCreateForm({ notes: '', items: [] }); }}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>发起调货申请</span>
          </button>
        )}
      </div>

      {/* 状态筛选 */}
      <div className="flex flex-wrap gap-2">
        {filterTabs.map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilterStatus(val)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterStatus === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 划拨单列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">加载中...</p>
        </div>
      ) : transfers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无划拨记录</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transfers.map(transfer => {
            const statusCfg = STATUS_CONFIG[transfer.status] || STATUS_CONFIG.pending;
            const isExpanded = expandedNo === transfer.transfer_no;
            const details = detailMap[transfer.transfer_no];

            return (
              <div key={transfer.transfer_no} className="bg-white rounded-xl shadow overflow-hidden">
                <div
                  className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleDetail(transfer.transfer_no)}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold">{transfer.transfer_no}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {transfer.from_store_name} → {transfer.to_store_name} ·
                        共 {transfer.item_count} 种 ·
                        {new Date(transfer.created_at).toLocaleDateString('zh-CN')}
                        {transfer.paid_amount > 0 && (
                          <span className="ml-2 text-green-600">已付 ${parseFloat(transfer.paid_amount).toFixed(2)}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
                    {/* 总店：确认发货 */}
                    {isHQ && transfer.status === 'pending' && (
                      <button onClick={() => handleShip(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700">
                        <Truck className="w-4 h-4" /><span>确认发货</span>
                      </button>
                    )}
                    {/* 总店：确认收款 */}
                    {isHQ && transfer.status === 'payment_pending' && (
                      <button onClick={() => handleConfirmPayment(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
                        <DollarSign className="w-4 h-4" /><span>确认收款</span>
                      </button>
                    )}
                    {/* 分店：确认收货 */}
                    {isManager && isBranch && transfer.status === 'shipped' && transfer.to_store_id === currentUser?.store_id && (
                      <button onClick={() => openReceive(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700">
                        <CheckCircle className="w-4 h-4" /><span>确认收货</span>
                      </button>
                    )}
                    {/* 分店：提交付款通知 */}
                    {isManager && isBranch && transfer.status === 'received' && transfer.to_store_id === currentUser?.store_id && (
                      <button onClick={() => openPay(transfer.transfer_no, transfer.total_amount)}
                        className="flex items-center space-x-1 bg-orange-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-orange-600">
                        <CreditCard className="w-4 h-4" /><span>提交付款</span>
                      </button>
                    )}
                    {/* 取消 */}
                    {transfer.status === 'pending' && (
                      <button onClick={() => handleCancel(transfer.transfer_no)}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-600 hover:bg-gray-50">
                        取消
                      </button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* 展开明细 */}
                {isExpanded && (
                  <div className="border-t px-5 py-4 bg-gray-50">
                    {details ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="text-left py-1">商品名称</th>
                            <th className="text-right py-1">数量</th>
                            <th className="text-right py-1">成本价</th>
                            <th className="text-right py-1">小计</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.map(item => (
                            <tr key={item.id} className="border-t border-gray-100">
                              <td className="py-2">{item.product_name}</td>
                              <td className="text-right py-2">{item.quantity}</td>
                              <td className="text-right py-2">${parseFloat(item.cost_price).toFixed(2)}</td>
                              <td className="text-right py-2">${parseFloat(item.total_amount).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-200 font-semibold">
                            <td className="py-2">合计</td>
                            <td className="text-right py-2">{details.reduce((s, i) => s + i.quantity, 0)}</td>
                            <td></td>
                            <td className="text-right py-2">${details.reduce((s, i) => s + parseFloat(i.total_amount), 0).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <p className="text-center text-gray-400 py-4">加载中...</p>
                    )}
                    {transfer.notes && <p className="text-sm text-gray-500 mt-3">备注：{transfer.notes}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 发起调货申请弹窗 */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">发起调货申请</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
                从总店有库存的商品中选择，申请发送给总店管理员审核后发货
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">申请商品 *</label>
                  <button onClick={addItem} className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1">
                    <Plus className="w-4 h-4" /><span>添加商品</span>
                  </button>
                </div>
                {createForm.items.length === 0 ? (
                  <div onClick={addItem} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400">
                    <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500 text-sm">点击添加商品</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {createForm.items.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                        <select
                          value={item.product_id}
                          onChange={e => updateItem(idx, 'product_id', e.target.value)}
                          className="flex-1 px-2 py-1.5 border rounded-lg text-sm"
                        >
                          <option value="">选择商品</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}（总店库存：{p.stock}）</option>
                          ))}
                        </select>
                        <input
                          type="number" min="1" max={item.stock}
                          value={item.quantity}
                          onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          className="w-24 px-2 py-1.5 border rounded-lg text-sm text-center"
                          placeholder="数量"
                        />
                        <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">备注</label>
                <textarea
                  value={createForm.notes}
                  onChange={e => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" rows={2}
                  placeholder="备注信息（可选）"
                />
              </div>
              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{createError}</div>
              )}
            </div>
            <div className="p-6 border-t flex space-x-3">
              <button onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">取消</button>
              <button onClick={handleCreate} disabled={creating}
                className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg ${creating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
                {creating ? '提交中...' : '提交申请'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 确认收货弹窗 */}
      {showReceive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">确认收货</h3>
              <button onClick={() => setShowReceive(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">请核对实际收到的数量，如有差异请修改：</p>
              <div className="space-y-2">
                {receiveItems.map((item, idx) => (
                  <div key={item.transfer_id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.product_name}</p>
                      <p className="text-xs text-gray-500">应收：{item.quantity}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">实收：</span>
                      <input type="number" min="0" max={item.quantity} value={item.actual_quantity}
                        onChange={e => { const n = [...receiveItems]; n[idx].actual_quantity = e.target.value; setReceiveItems(n); }}
                        className="w-20 px-2 py-1 border rounded-lg text-sm text-center"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">收货备注</label>
                <textarea value={receiveNotes} onChange={e => setReceiveNotes(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} placeholder="如有差异请说明（可选）" />
              </div>
            </div>
            <div className="p-6 border-t flex space-x-3">
              <button onClick={() => setShowReceive(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">取消</button>
              <button onClick={handleReceive} disabled={receiving}
                className={`flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg ${receiving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700'}`}>
                {receiving ? '确认中...' : '确认收货'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 提交付款通知弹窗 */}
      {showPay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">提交付款通知</h3>
              <button onClick={() => setShowPay(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-sm text-orange-700">
                填写实际付款金额，通知总店核对收款
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">付款金额 *</label>
                <input type="number" min="0" step="0.01" value={payForm.paid_amount}
                  onChange={e => setPayForm(prev => ({ ...prev, paid_amount: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">付款备注</label>
                <textarea value={payForm.notes} onChange={e => setPayForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" rows={2}
                  placeholder="如：已转账至KHQR，凭证截图已发微信（可选）" />
              </div>
            </div>
            <div className="p-6 border-t flex space-x-3">
              <button onClick={() => setShowPay(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">取消</button>
              <button onClick={handlePay} disabled={paying}
                className={`flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg ${paying ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'}`}>
                {paying ? '提交中...' : '提交付款通知'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTransfersPage;